import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/axiosInstance";

const EASTERN = "America/New_York";
const currentDay = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const part = (name) => parts.find((value) => value.type === name).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};
const timeLabel = (meeting) => new Intl.DateTimeFormat("en-US", {
  timeZone: EASTERN, hour: "numeric", minute: "2-digit",
}).format(new Date(meeting.sessionTime));

export default function AdminMeetingCalendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const today = currentDay();
  const [month, setMonth] = useState(location.state?.calendarMonth || today.slice(0, 7));
  const [selectedDay, setSelectedDay] = useState(today);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setMeetings([]);
    axios.get("/api/admin/calendar", { params: { month }, signal: controller.signal })
      .then(({ data }) => {
        if (!Array.isArray(data.meetings)) throw new Error("Invalid calendar");
        if (!controller.signal.aborted) setMeetings(data.meetings);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("Unable to load the calendar. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [month, refreshCount]);

  const [year, monthNumber] = month.split("-").map(Number);
  const firstDay = new Date(year, monthNumber - 1, 1).getDay();
  const dayCount = new Date(year, monthNumber, 0).getDate();
  const monthLabel = new Date(year, monthNumber - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const activeDay = selectedDay.startsWith(month) ? selectedDay : `${month}-01`;
  const dayMeetings = meetings.filter((meeting) => meeting.sessionTimeLocal.startsWith(activeDay));
  const moveMonth = (delta) => {
    const date = new Date(year, monthNumber - 1 + delta, 1);
    setMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
  };
  const openMeeting = (meeting) => navigate("/admin/schedule", { state: { calendarMeeting: meeting } });
  const meetingButton = (meeting, compact = false) => (
    <button
      key={meeting.meetingID}
      type="button"
      disabled={!meeting.canReschedule}
      onClick={() => openMeeting(meeting)}
      aria-label={`Reschedule ${meeting.clients.map((client) => client.name).join(", ")} with ${meeting.therapist.name} at ${timeLabel(meeting)}`}
      className={`w-full rounded-lg border border-blue-100 bg-blue-50 text-left text-blue-950 transition hover:bg-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60 ${compact ? "space-y-1 p-2 text-xs leading-snug" : "space-y-1 p-4 text-sm"}`}
    >
      <span className="block whitespace-nowrap font-semibold text-blue-800">{timeLabel(meeting)}</span>
      <span className="block break-words font-semibold">
        {meeting.clients.map((client) => client.name).join(", ")}
      </span>
      <span className="block break-words text-gray-600">With {meeting.therapist.name}</span>
      {!compact && !meeting.canReschedule && <span className="mt-2 block text-amber-800">An account email is unavailable; this session cannot be rescheduled here.</span>}
    </button>
  );

  return (
    <section className="border-t border-gray-200 pt-6" aria-label="Meeting calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Meeting calendar</h3>
          <p className="mt-1 text-sm text-gray-600">All times Eastern. Select a session to reschedule.</p>
        </div>
        <button type="button" onClick={() => setRefreshCount((value) => value + 1)} className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50" disabled={loading}>Refresh calendar</button>
      </div>
      {location.state?.calendarNotice && <p role="status" className="mt-3 rounded-lg bg-green-50 p-3 text-green-800">{location.state.calendarNotice}</p>}
      <div className="my-5 flex flex-wrap items-center justify-between gap-3">
        <h4 className="text-lg font-semibold" aria-live="polite">{monthLabel}</h4>
        <div className="flex gap-2">
          <button type="button" aria-label="Previous month" onClick={() => moveMonth(-1)} className="rounded-lg border px-3 py-2 hover:bg-gray-50">←</button>
          <button type="button" onClick={() => { setMonth(today.slice(0, 7)); setSelectedDay(today); }} className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">Today</button>
          <button type="button" aria-label="Next month" onClick={() => moveMonth(1)} className="rounded-lg border px-3 py-2 hover:bg-gray-50">→</button>
        </div>
      </div>
      {loading && <p role="status" className="mb-3 text-sm text-gray-600">Loading sessions…</p>}
      {error && <p role="alert" className="mb-3 rounded-lg bg-red-50 p-3 text-red-800">{error}</p>}
      <div className="mb-5 sm:hidden">
        <label htmlFor="admin-calendar-day" className="mb-2 block text-sm font-semibold text-gray-700">
          View sessions by date
        </label>
        <input
          id="admin-calendar-day"
          type="date"
          min={`${month}-01`}
          max={`${month}-${String(dayCount).padStart(2, "0")}`}
          value={activeDay}
          onChange={(event) => setSelectedDay(event.target.value)}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-base"
        />
      </div>
      <div className="hidden overflow-x-auto rounded-xl border border-gray-200 sm:block">
        <div className="grid min-w-[980px] grid-cols-7">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <div key={day} className="bg-gray-50 px-2 py-3 text-center text-xs font-semibold text-gray-600">{day}</div>)}
          {Array.from({ length: Math.ceil((firstDay + dayCount) / 7) * 7 }, (_, index) => {
            const day = index - firstDay + 1;
            if (day < 1 || day > dayCount) return <div key={index} className="min-h-[120px] border-t border-r border-gray-100 bg-gray-50/50" />;
            const date = `${month}-${String(day).padStart(2, "0")}`;
            const events = meetings.filter((meeting) => meeting.sessionTimeLocal.startsWith(date));
            return (
              <div key={date} className={`min-h-[120px] space-y-1 border-t border-r border-gray-100 p-2 ${date === activeDay ? "bg-blue-50/40" : "bg-white"}`}>
                <button type="button" aria-label={`View sessions for ${date}`} aria-pressed={date === activeDay} onClick={() => setSelectedDay(date)} className={`mb-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${date === today ? "bg-blue-600 text-white" : "hover:bg-gray-100"}`}>{day}</button>
                {events.slice(0, 2).map((meeting) => meetingButton(meeting, true))}
                {events.length > 2 && <button type="button" onClick={() => setSelectedDay(date)} className="text-xs font-medium text-blue-700">+{events.length - 2} more</button>}
              </div>
            );
          })}
        </div>
      </div>
      {!loading && !error && <p className="mt-3 text-sm text-gray-500">{meetings.length} {meetings.length === 1 ? "session" : "sessions"} this month</p>}
      <div className="mt-6">
        <h4 className="mb-3 font-semibold text-gray-800">Sessions on {activeDay}</h4>
        <div className="space-y-2">{dayMeetings.map((meeting) => meetingButton(meeting))}</div>
        {!loading && !error && dayMeetings.length === 0 && <p className="text-sm text-gray-500">No sessions scheduled for this day.</p>}
      </div>
    </section>
  );
}
